'use strict'

// OOTB Imports
import {useQuery} from '@tanstack/react-query'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import useMultiSite from '@app/hooks/use-multi-site'
import {useLocation} from 'react-router-dom'

// Custom APIs
import UrlLifeCycleMgr from '../custom-api/url-life-cycle-custom-api'

/**
 * Custom hook to manage the lifecycle of a category URL, determining whether to redirect
 * to a category page or navigate up the catalog hierarchy based on the category's status.
 *
 * @param {string} categoryId - The unique identifier of the category to evaluate.
 * @returns {Object} - The result of the useQuery hook, containing the category URL lifecycle data.
 *
 * @description
 * This hook utilizes the `useQuery` hook to fetch and manage the lifecycle data of a category URL.
 * It checks if the category is online and has sellable products. If the category is offline or
 * lacks sellable products, it attempts to navigate up one level in the catalog hierarchy and
 * re-evaluate the conditions. The hook is designed to work in a multi-site environment, using
 * the current site and locale information. It also handles server-side rendering by fetching
 * an access token when necessary.
 *
 * @example
 * const { data, error, isLoading } = useCategoryUrlLifeCycle('12345');
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <ErrorComponent />;
 * // Use the data to render the category page or handle redirection logic.
 */
const useCategoryUrlLifeCycle = (categoryId) => {
    const {site, locale} = useMultiSite()
    const location = useLocation()
    const urlParams = new URLSearchParams(location.search)
    let searchQuery = urlParams.get('q')
    const isSearch = !!searchQuery
    const {getTokenWhenReady} = useAccessToken()

    /*
        Use useQuery to fetch Category URL Life Cycle data
        - case 1.1 - the corresponding category is online and has sellable products in it → redirect to it
        - case 1.2 - the corresponding category is offline or does not have sellable products → go up one level in the catalog hierarchy 
        and try case 1.1 again
    */
    return useQuery({
        queryKey: ['categoryUrlLifeCycle', categoryId],
        queryFn: async () => {
            let result = null
            if (isServer && categoryId && !isSearch) {
                const accessToken = await getTokenWhenReady()
                const {app: appConfig} = getConfig()
                const api = new UrlLifeCycleMgr({
                    parameters: {
                        siteId: site.id,
                        organizationId: appConfig.commerceAPI.parameters.organizationId,
                        locale: locale.id
                    }
                })
                result = await api.categoryUrlLifeCycle(site.id, accessToken, {
                    categoryId: categoryId
                })
            }

            return result
        }
    })
}

export default useCategoryUrlLifeCycle
